import {
	type ApplicationMode,
	type MarketViewMonitoringMetric,
	type MetricCategory,
	type MonitoringMetric,
	type UserInstrumentClassificationDto,
	ConstraintRelation,
} from "@eu.mdotm.api/sphere";
import { IconWalls } from "$root/components/IconWall";
import { asOfMissingDataWall } from "$root/pages/PortfolioDetails/as-of/missingDataWall";
import { typedUrlForRoute } from "$root/components/PlatformRouter/RoutesDef";
import { aclByArea } from "$root/functional-areas/acl/checkers/all";
import { userClassifications$ } from "$root/functional-areas/instruments/user-classifications";
import { portfolioRepository } from "$root/functional-areas/portfolio/repository";
import { isAsOfReadable } from "$root/pages/PortfolioDetails/as-of/useAsOfSnapshot";
import { useQuery } from "@tanstack/react-query";
import { useUserValue } from "$root/functional-areas/user";
import { useLocaleFormatters } from "$root/localization/hooks";
import { platformToast } from "$root/notification-system/toast";
import { tagColorMap } from "$root/ui-lib/table/colors";
import { TagBadge } from "$root/ui-lib/table/custom-column/tags/Tag";
import {
	type PortfolioWidgetData,
	PortfolioQueryWidgetBase,
	WidgetStatus,
	portfolioWidgetMissingDataReason,
} from "$root/pages/PortfolioDetails/PortfolioWidgetStatus";
import { type ContextContent, downloadContentDisposition, qualifier, sumArrayLike, withContexts } from "$root/utils";
import { runWithErrorReporting } from "$root/api/error-reporting";
import { workspaceRepository } from "$root/functional-areas/workspace/repository";
import { WidgetDownloadTrigger } from "$root/components/DropdownMenuTrigger";
import { buildWidgetExportConfig } from "./widget-export";
import { unreachable, UnreachableError } from "@mdotm/utils/runtime";
import { useMappedQuery } from "$root/third-party-integrations/react-query/hooks";
import { useWidgetOptions } from "$root/widgets-architecture/layout/WidgetsMapper/context";
import {
	ActionText,
	CollapsibleBase,
	ComputedSizeContainer,
	Controller,
	Dialog,
	DialogFooter,
	Icon,
	ScrollWrapper,
	Svg,
	Text,
	defaultTailwindTransitionDurationMs,
} from "@mdotm/shadcn/legacy/components";
import { Switch, generateUniqueDOMId, toClassName } from "@mdotm/shadcn/legacy/react-extensions";
import { ForEach, useUnsafeUpdatedRef } from "@mdotm/utils/react-extra";
import { useAtomValue } from "@mdotm/shadcn/legacy/reactivity";
import { themeCSSVars } from "@mdotm/shadcn/legacy/themes";
import { builtInSortFnFor, clamp, stableEmptyArray, switchExpr } from "@mdotm/shadcn/legacy/utils";
import { groupBy } from "@mdotm/utils/collections";
import type { TFunction } from "i18next";
import { type CSSProperties, type ReactNode, useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { match } from "ts-pattern";
import { PortfolioContext } from "../contexts/portfolio";
import { DropdownMenu, type DropdownTriggerProps, Button } from "@mdotm/shadcn/mdotm/components";

function FilterTrigger({ state: _state, ...props }: DropdownTriggerProps): React.JSX.Element {
	return (
		<button type="button" {...props}>
			<Icon icon="Filter-no" size={20} color={themeCSSVars.palette_P500} />
		</button>
	);
}

// `TAG_RELATIVE_TO_TAG` and `COMPOSITION_PRIORITY` are now part of the backend `MetricCategory` enum.
// `GROUP_ASSET_ALLOCATION` is still a frontend-only category.
type CustomMetricCategory = MetricCategory | "GROUP_ASSET_ALLOCATION";
export type PortfolioMonitoringBlockEntry = {
	groupId: string;
	severity: PortfolioMonitoringSeverity;
	type: CustomMetricCategory;
	applicationMode: ApplicationMode | undefined; // undefined when risk model, defined when custom column
	subType: string;
	classificationOption?: string;
	value: number;
	groupValue?: number;
	isSoftConstraint: boolean;
	showStepValue?: boolean;
	hidePercentage?: boolean;
	thresholds: { min?: number; max?: number };
	warningDelta: { min?: number; max?: number };
	status?: "processing" | "ok";
} & (
	| {
			constraintRelation: "MIN";
			min: [/* threshold */ number, /* warning threshold */ number];
			target?: undefined;
			max?: undefined;
	  }
	| {
			constraintRelation: "MAX";
			min?: undefined;
			target?: undefined;
			max: [/* warning threshold */ number, /* threshold */ number];
	  }
	| {
			constraintRelation: "BETWEEN" | "RELATIVE_TO_REFERENCE";
			min: [/* threshold */ number, /* warning threshold */ number];
			target?: undefined;
			max: [/* warning threshold */ number, /* threshold */ number];
	  }
	| {
			constraintRelation: "EQUAL";
			min: [/* threshold */ number, /* warning threshold */ number];
			target: number;
			max: [/* warning threshold */ number, /* threshold */ number];
	  }
);

type MonitoringSeverity = "ok" | "close-to" | "breached";
export function getSeverityByRelation(
	relation: Exclude<ConstraintRelation, "EXCLUDE">,
	metric: {
		value?: number;
		minThreshold?: number;
		maxThreshold?: number;
		minWarningDelta?: number;
		maxWarningDelta?: number;
		target?: boolean;
	},
): MonitoringSeverity {
	const { value = 0, maxThreshold = 100, minThreshold = 0, maxWarningDelta = 0, minWarningDelta = 0 } = metric;

	if (metric.target) {
		return "ok";
	}

	function exceedMaxThreshold(constraintValue: number, max: number) {
		return constraintValue > max;
	}

	function exceedMinThreshold(constraintValue: number, min: number) {
		return constraintValue < min;
	}

	function closeToMinThreshold(constraintValue: number, opts: { minWarningDelta: number; minThreshold: number }) {
		return constraintValue - opts.minWarningDelta < opts.minThreshold;
	}

	function closeToMaxThreshold(constraintValue: number, opts: { maxWarningDelta: number; maxThreshold: number }) {
		return constraintValue + opts.maxWarningDelta > opts.maxThreshold;
	}

	function equalsThreshold(constraintValue: number, threshold: number) {
		return constraintValue === threshold;
	}

	return switchExpr(relation, {
		RELATIVE_TO_REFERENCE: () =>
			exceedMinThreshold(value, minThreshold) || exceedMaxThreshold(value, maxThreshold)
				? "breached"
				: closeToMinThreshold(value, { minThreshold, minWarningDelta }) ||
						closeToMaxThreshold(value, { maxWarningDelta, maxThreshold })
					? "close-to"
					: "ok",

		BETWEEN: () =>
			exceedMinThreshold(value, minThreshold) || exceedMaxThreshold(value, maxThreshold)
				? "breached"
				: closeToMinThreshold(value, { minThreshold, minWarningDelta }) ||
						closeToMaxThreshold(value, { maxWarningDelta, maxThreshold })
					? "close-to"
					: "ok",
		EQUAL: () => (equalsThreshold(value, minThreshold) || equalsThreshold(value, maxThreshold) ? "ok" : "breached"),
		// exceedMinThreshold(value, minThreshold - minWarningDelta) ||
		// exceedMaxThreshold(value, minThreshold + minWarningDelta)
		// 	? "breached"
		// 	: closeToMinThreshold(value, { minThreshold, minWarningDelta }) ||
		// 	    closeToMaxThreshold(value, { maxWarningDelta: minThreshold, maxThreshold: minWarningDelta })
		// 	  ? "ok"
		// 	  : "ok",
		MAX: () =>
			exceedMaxThreshold(value, maxThreshold)
				? "breached"
				: closeToMaxThreshold(value, { maxWarningDelta, maxThreshold })
					? "close-to"
					: "ok",
		MIN: () =>
			exceedMinThreshold(value, minThreshold)
				? "breached"
				: closeToMinThreshold(value, { minThreshold, minWarningDelta })
					? "close-to"
					: "ok",
	});
}

// TODO: mock-only — until the backend provides tag colors for the tagRelativeToTag constraint,
// derive a stable pseudo-random color per tag label so the badges don't flicker between renders.
const mockTagBadgeColors = [
	tagColorMap.DEEP_RED,
	tagColorMap.ORANGE,
	tagColorMap.YELLOW,
	tagColorMap.LIGHT_BLUE,
	tagColorMap.BLUEBERRY,
	tagColorMap.VIOLET,
	tagColorMap.GREEN,
	tagColorMap.BRIGHT_RED,
].map((color) => color.badgeColor());

function mockTagColor(label: string): string {
	let hash = 0;
	for (let i = 0; i < label.length; i++) {
		hash = (hash * 31 + label.charCodeAt(i)) | 0;
	}
	return mockTagBadgeColors[Math.abs(hash) % mockTagBadgeColors.length];
}

/** @internal — exported for tests */
export function isMetricTypeUnitless(metric: CustomMetricCategory) {
	const UNITLESS_METRICS = new Set(["NUMBER_OF_INSTRUMENTS", "AVERAGE_SCORE", "SCORE"]);
	return UNITLESS_METRICS.has(metric);
}

/** @internal — exported for tests */
export function createConstraintRelationInfo(monitoringEntry: PortfolioMonitoringBlockEntry): string {
	const isUnitlessValue = isMetricTypeUnitless(monitoringEntry.type);
	const numberUnit = isUnitlessValue ? "" : "%";
	switch (monitoringEntry.constraintRelation) {
		case "RELATIVE_TO_REFERENCE":
			return `Δ Min: ${monitoringEntry.thresholds.min}${numberUnit} Δ Max: ${monitoringEntry.thresholds.max}${numberUnit}`;
		case "BETWEEN":
			return `Min: ${monitoringEntry.thresholds.min}${numberUnit} Max: ${monitoringEntry.thresholds.max}${numberUnit}`;
		case "EQUAL":
			return `Eq: ${monitoringEntry.thresholds.min}${numberUnit}`;
		case "MAX":
			return `Max: ${monitoringEntry.thresholds.max}${numberUnit}`;
		case "MIN":
			return `Min: ${monitoringEntry.thresholds.min}${numberUnit}`;
		default:
			unreachable(monitoringEntry);
	}
}

const hasApplicationMode = {
	ASSET_ALLOCATION: true,
	CURRENCY: true,
	TAG: true,
	AVERAGE_SCORE: false,
	FOR_EACH: false,
	INSTRUMENT: false,
	LOCK_INSTRUMENT: false,
	NUMBER_OF_INSTRUMENTS: false,
	RISK: false,
	SCORE: false,
	TARGET_TRACKING_ERROR: false,
	TARGET_VOLATILITY: false,
	WEIGHT_ON_SINGLE_INSTRUMENT: false,
	GROUP_ASSET_ALLOCATION: false,
	TAG_RELATIVE_TO_TAG: false,
	COMPOSITION_PRIORITY: false,
	MAX_TURNOVER: false,
	ROUNDING: false,
} satisfies Record<CustomMetricCategory, boolean>;

const excludeSoftConstraint: CustomMetricCategory[] = ["NUMBER_OF_INSTRUMENTS", "WEIGHT_ON_SINGLE_INSTRUMENT"];
const excludeConstraintWithoutRelation: CustomMetricCategory[] = [];

/** @internal — exported for tests */
export function createConstraintInfo({
	excludeSoftConstraint,
	excludeConstraintWithoutRelation,
	monitoringEntry,
	t,
}: {
	monitoringEntry: PortfolioMonitoringBlockEntry;
	excludeSoftConstraint: CustomMetricCategory[];
	excludeConstraintWithoutRelation: CustomMetricCategory[];
	t: TFunction;
}) {
	const applicationModeLabel = !hasApplicationMode[monitoringEntry.type]
		? ""
		: monitoringEntry.applicationMode
			? t(`MONITORING_METRICS.APPLICATION_MODE.${monitoringEntry.applicationMode}`)
			: "";

	const canPrintSoft = !excludeSoftConstraint.includes(monitoringEntry.type);
	const relation = excludeConstraintWithoutRelation.includes(monitoringEntry.type)
		? ""
		: createConstraintRelationInfo(monitoringEntry);
	const softLabel = monitoringEntry.isSoftConstraint && canPrintSoft ? "Soft" : "";

	if (softLabel) {
		return [applicationModeLabel, relation, softLabel].filter((txt) => txt).join(" - ");
	}
	return [applicationModeLabel, relation].filter((txt) => txt).join(" - ");
}

/** What the monitoring payload hands the widget body: its metrics, plus the classifications naming them. */
type PortfolioMonitoringWidgetData = {
	marketViewMonitoringMetric?: MarketViewMonitoringMetric;
	monitoringMetrics: Array<MonitoringMetric>;
	classifications: UserInstrumentClassificationDto[];
};

/**
 * A metric drives a monitoring row only when it carries a relation other than Exclude, so a payload
 * holding none of those has nothing to monitor. Kept next to the widget's mapper, which decides the
 * empty wall from it, and the `rows` pipeline below, which filters on the same condition.
 */
function hasMonitorableConstraint(monitoringMetrics: Array<MonitoringMetric>): boolean {
	return monitoringMetrics.some((metric) => metric.relation && metric.relation !== ConstraintRelation.Exclude);
}

/** The market view's forecast window has closed, which the table area announces with its own banner. */
function marketViewExpired(marketViewMonitoringMetric?: MarketViewMonitoringMetric): boolean {
	if (marketViewMonitoringMetric?.forecastDates?.to === undefined) {
		return false;
	}

	return new Date(marketViewMonitoringMetric.forecastDates.to).getTime() < Date.now();
}

function PortfolioMonitoringBlock(props: ContextContent<typeof PortfolioContext>): React.JSX.Element {
	const { t } = useTranslation();

	useWidgetOptions(
		() => ({
			title: t("PORTFOLIO_MONITORING.TITLE"),
		}),
		[t],
	);

	const queryRaw = useQuery({
		...portfolioRepository.monitoring.getQueryConfig(props.portfolio?.uuid ?? "", {
			enhanced: props.enhanced,
			asOf: props.asOf,
		}),
		enabled: isAsOfReadable(props.asOf, props.asOfSnapshot),
	});

	const classifications = useAtomValue(userClassifications$);
	const query = useMappedQuery(
		(metrics): PortfolioWidgetData<PortfolioMonitoringWidgetData | undefined> => {
			if (!metrics?.monitoringMetrics) {
				return {
					data: undefined,
					widgetStatus: portfolioWidgetMissingDataReason(props.portfolio, "PortfolioMonitoringBlock"),
				};
			}

			const data = {
				marketViewMonitoringMetric: metrics?.marketViewMonitoringMetric,
				monitoringMetrics: metrics.monitoringMetrics,
				classifications,
			};

			return {
				data,
				widgetStatus: WidgetStatus.READY,
			};
		},
		[props.portfolio, classifications],
		queryRaw,
	);

	return (
		<PortfolioQueryWidgetBase
			query={query}
			// The live summary's answer for an executing enhancement, where no payload arrived at all. A
			// payload that arrived carrying no constraint is a different wall, chosen in the body below.
			iconWalls={{ empty: <IconWalls.PortfolioMonitoringHiddenV2 opaque /> }}
		>
			{({ monitoringMetrics, marketViewMonitoringMetric, classifications }) => {
				// No constraint to monitor and nothing to show — unless the market view has expired, which is not
				// empty: the table area below still carries that banner.
				if (
					!hasMonitorableConstraint(monitoringMetrics ?? stableEmptyArray) &&
					!marketViewExpired(marketViewMonitoringMetric)
				) {
					return (
						asOfMissingDataWall(props.asOf) ??
						(props.portfolio?.status === "PROPOSAL_READY" ? (
							<IconWalls.PortfolioMonitoringUnconsistencyV2 opaque />
						) : (
							<IconWalls.PortfolioMonitoringEmptyDataV2 opaque onCreate={() => props.requestEnhance?.()} />
						))
					);
				}

				return (
					<PortfolioMonitoringBlockInner
						{...props}
						marketViewMonitoringMetric={marketViewMonitoringMetric}
						monitoringMetrics={monitoringMetrics ?? stableEmptyArray}
						classifications={classifications}
					/>
				);
			}}
		</PortfolioQueryWidgetBase>
	);
}

export function PortfolioMonitoringBlockInner({
	monitoringMetrics,
	portfolio,
	enhanced,
	asOf,
	marketViewMonitoringMetric,
	classifications,
}: ContextContent<typeof PortfolioContext> & {
	monitoringMetrics: Array<MonitoringMetric>;
	marketViewMonitoringMetric?: MarketViewMonitoringMetric;
	classifications: UserInstrumentClassificationDto[];
}): React.JSX.Element {
	const [filter, setFilter] = useState<"hide-ok" | "breached-first" | "risk-first" | null>("breached-first");
	const [isModalOpen, setIsModalOpen] = useState(false);
	const user = useUserValue();
	const { t } = useTranslation();

	const riskConstraintMap = useMemo(() => t(`VAR`, { returnObjects: true }), [t]);
	// A COMPOSITION_PRIORITY metric carries no relation, so it never reaches the constraint `rows` pipeline
	// (which filters on `metric.relation`); it only drives the dedicated "Composition has priority" row.
	const compositionHasPriority = useMemo(
		() => monitoringMetrics.some((metric) => metric.category === "COMPOSITION_PRIORITY"),
		[monitoringMetrics],
	);
	const { richAcl } = portfolio ?? {};
	const createMetricSubType = useCallback(
		(params: {
			entity?: string;
			relation?: ConstraintRelation;
			target?: boolean;
			classificationOption?: string;
			category?: CustomMetricCategory;
		}) => {
			if (!params.entity || !params.relation) {
				return "";
			}

			if (params.category === "SCORE") {
				const matchedScoreName = classifications.find((x) => x.classificationUuid === params.entity)?.name;
				if (matchedScoreName) {
					return matchedScoreName;
				}
			}

			return params.entity;
		},
		[classifications],
	);

	const confirmOptimization = useCallback(async () => {
		try {
			if (!portfolio || !portfolio.uuid) {
				console.warn("missing portfolioData, confirm ignored.");
				return;
			}
			await portfolioRepository.fixVar(portfolio.uuid);

			platformToast({
				children: t("PORTFOLIOS.OPTIMIZE_ACCEPTED"),
				severity: "success",
				icon: "Portfolio",
			});
		} catch (error) {
			platformToast({
				children: "There are some conflicting constraints, please review your portfolio configuration",
				severity: "warning",
				icon: "Portfolio",
			});
			throw error;
		} finally {
			setIsModalOpen(false);
		}
	}, [portfolio, t]);

	const rows = useMemo<Array<PortfolioMonitoringBlockEntry> | undefined>(
		() =>
			monitoringMetrics
				?.flatMap((metric) =>
					metric.relation && metric.relation !== ConstraintRelation.Exclude
						? [
								{
									...metric,
									entity:
										metric.category === "RISK" && metric.entity
											? riskConstraintMap[metric.entity as keyof typeof riskConstraintMap]
											: metric.entity,
								},
							]
						: [],
				)
				.map((metric) => {
					const between = (constraintRelation: "BETWEEN" | "RELATIVE_TO_REFERENCE") => () => {
						const minBreach = metric.minThreshold ?? 0;
						const maxBreach = metric.maxThreshold ?? 0;
						const minCloseTo = Math.max(
							minBreach,
							Math.min(minBreach + (metric.minWarningDelta ?? 0), maxBreach - (metric.maxWarningDelta ?? 0)),
						);
						const maxCloseTo = Math.min(
							maxBreach,
							Math.max(minBreach + (metric.minWarningDelta ?? 0), maxBreach - (metric.maxWarningDelta ?? 0)),
						);
						return {
							groupId: metric.identifier ?? generateUniqueDOMId(),
							constraintRelation,
							min: [minBreach, minCloseTo] as [number, number],
							max: [maxCloseTo, maxBreach] as [number, number],
							type: metric.category!,
							subType: createMetricSubType({
								entity: metric.entity,
								target: metric.target,
								relation: metric.relation,
								classificationOption: metric.classificationOption,
								category: metric.category,
							}),
							isSoftConstraint: Boolean(metric.target),
							severity: getSeverityByRelation("BETWEEN", metric),
							value: metric.value ?? 0,
							applicationMode: metric.applicationMode,
							thresholds: { max: metric.maxThreshold, min: metric.minThreshold },
							warningDelta: { max: metric.maxWarningDelta, min: metric.minWarningDelta },
							classificationOption: metric.classificationOption,
							groupValue: metric.groupValue,
							status: metric.value === null ? "processing" : "ok",
						} satisfies PortfolioMonitoringBlockEntry;
					};
					return switchExpr<ConstraintRelation, Record<ConstraintRelation, () => PortfolioMonitoringBlockEntry>>(
						metric.relation!,
						{
							[ConstraintRelation.Exclude]: () => {
								throw new UnreachableError();
							},
							[ConstraintRelation.RelativeToReference]: between("RELATIVE_TO_REFERENCE"),
							[ConstraintRelation.Between]: between("BETWEEN"),
							[ConstraintRelation.Equal]: () => {
								const target = metric.minThreshold ?? 0;
								const distance = metric.minWarningDelta ?? 0;
								const minBreach = Math.max(0, target - distance * 2);
								const maxBreach = Math.min(100, target + distance * 2);
								const minCloseTo = Math.max(minBreach, target - distance);
								const maxCloseTo = Math.min(maxBreach, target + distance);

								return {
									groupId: metric.identifier ?? generateUniqueDOMId(),
									constraintRelation: "EQUAL",
									type: metric.category!,
									subType: createMetricSubType({
										entity: metric.entity,
										target: metric.target,
										relation: metric.relation,
										classificationOption: metric.classificationOption,
										category: metric.category,
									}),
									isSoftConstraint: Boolean(metric.target),
									severity: getSeverityByRelation("EQUAL", metric),
									value: metric.value ?? 0,
									// EQUAL doesn't use maxThreshold
									min: [minBreach, minCloseTo],
									target,
									max: [maxCloseTo, maxBreach],
									applicationMode: metric.applicationMode,
									thresholds: { max: metric.maxThreshold, min: metric.minThreshold },
									warningDelta: { max: metric.maxWarningDelta, min: metric.minWarningDelta },
									classificationOption: metric.classificationOption,
									groupValue: metric.groupValue,
									status: metric.value === null ? "processing" : "ok",
								};
							},
							[ConstraintRelation.Min]: () => ({
								groupId: metric.identifier ?? generateUniqueDOMId(),
								constraintRelation: "MIN",
								min: [
									metric.minThreshold ?? 0,
									Math.max(metric.minThreshold ?? 0, (metric.minThreshold ?? 0) + (metric.minWarningDelta ?? 0)),
								],
								type: metric.category!,
								subType: createMetricSubType({
									entity: metric.entity,
									target: metric.target,
									relation: metric.relation,
									classificationOption: metric.classificationOption,
									category: metric.category,
								}),
								isSoftConstraint: Boolean(metric.target),
								severity: getSeverityByRelation("MIN", metric),
								value: metric.value ?? 0,
								applicationMode: metric.applicationMode,
								thresholds: { max: metric.maxThreshold, min: metric.minThreshold },
								warningDelta: { max: metric.maxWarningDelta, min: metric.minWarningDelta },
								classificationOption: metric.classificationOption,
								groupValue: metric.groupValue,
								status: metric.value === null ? "processing" : "ok",
							}),
							[ConstraintRelation.Max]: () => ({
								groupId: metric.identifier ?? generateUniqueDOMId(),
								constraintRelation: "MAX",
								max: [
									Math.min(metric.maxThreshold ?? 100, (metric.maxThreshold ?? 100) - (metric.maxWarningDelta ?? 0)),
									metric.maxThreshold ?? 100,
								],
								type: metric.category!,
								subType: createMetricSubType({
									entity: metric.entity,
									target: metric.target,
									relation: metric.relation,
									classificationOption: metric.classificationOption,
									category: metric.category,
								}),
								isSoftConstraint: Boolean(metric.target),
								severity: getSeverityByRelation("MAX", metric),
								value: metric.value ?? 0,
								applicationMode: metric.applicationMode,
								thresholds: { max: metric.maxThreshold, min: metric.minThreshold },
								warningDelta: { max: metric.maxWarningDelta, min: metric.minWarningDelta },
								classificationOption: metric.classificationOption,
								groupValue: metric.groupValue,
								status: metric.value === null ? "processing" : "ok",
							}),
						},
					);
				}),
		[createMetricSubType, monitoringMetrics, riskConstraintMap],
	);
	const areSomeAlertBreached = useMemo(() => rows?.some((row) => row.severity === "breached"), [rows]);

	const filteredRows = useMemo(
		() =>
			rows
				?.filter(({ severity }) => filter !== "hide-ok" || severity !== "ok")
				.sort(
					filter === "breached-first"
						? builtInSortFnFor("severity")
						: (a, b) => {
								if (a.severity === "close-to" && b.severity !== "close-to") {
									return -1;
								}
								if (a.severity !== "close-to" && b.severity === "close-to") {
									return 1;
								}
								return 0;
							},
				) ?? [],
		[filter, rows],
	);

	const groupedRows = Object.entries(groupBy(filteredRows, (x) => x.groupId)).map(([, group]) => group);
	const isMarketViewExpired = useMemo(
		() => marketViewExpired(marketViewMonitoringMetric),
		[marketViewMonitoringMetric],
	);

	// Download — mirrors the visible constraints via the smart-workspace `investmentMonitoringMetric` data provider.
	const exportMonitoring = async (investmentUuid: string) => {
		const config = buildWidgetExportConfig(
			"PortfolioMonitoringBlock",
			{
				investmentUuid,
				source: enhanced ? "PROPOSAL" : "CURRENT",
			},
			{ asOf },
		);
		await runWithErrorReporting(
			async () => {
				const file = await workspaceRepository.exportWorkspaceDataRaw.fetchQuery(config);
				await downloadContentDisposition(file);
			},
			{
				area: "portfolio",
				attemptedOperation: {
					message: "export portfolio monitoring",
					payload: JSON.stringify(config),
				},
			},
		);
	};
	const exportMonitoringRef = useUnsafeUpdatedRef(exportMonitoring);

	const actionHeaderMemo = useMemo(
		() => (
			<div className="flex items-center gap-2">
				<DropdownMenu
					Trigger={FilterTrigger}
					actions={[
						{
							label: "Hide OK",
							disabled: !rows?.some(({ severity }) => severity === "ok"),
							onClick: () => {
								setFilter((f) => (f === "hide-ok" ? null : "hide-ok"));
							},
							icon: filter === "hide-ok" ? "Outline" : undefined,
						},
						{
							label: "Show breached first",
							onClick: () => {
								setFilter((f) => (f === "breached-first" ? null : "breached-first"));
							},
							icon: filter === "breached-first" ? "Outline" : undefined,
						},
						{
							label: "Show risk first",
							onClick: () => {
								setFilter((f) => (f === "risk-first" ? null : "risk-first"));
							},
							icon: filter === "risk-first" ? "Outline" : undefined,
						},
					]}
				/>
				{portfolio?.uuid && rows && rows.length > 0 && (
					<DropdownMenu
						Trigger={WidgetDownloadTrigger}
						extraTriggerProps={{ "data-qualifier": qualifier.widgets.portfolioMonitoring.downloadButton }}
						actions={[
							{
								icon: "xls",
								onClickAsync: async () => {
									await exportMonitoringRef.current(portfolio.uuid);
								},
								label: t("PORTFOLIO_MONITORING.DOWNLOAD_TITLE"),
								"data-qualifier": qualifier.widgets.portfolioMonitoring.downloadItem,
							},
						]}
					/>
				)}
			</div>
		),
		[filter, rows, portfolio?.uuid, exportMonitoringRef, t],
	);

	useWidgetOptions(
		() => ({
			actionHeader: actionHeaderMemo,
			title: t("PORTFOLIO_MONITORING.TITLE"),
		}),
		[actionHeaderMemo, t],
	);

	const canCreateProposal = aclByArea.portfolio.canCreateProposal(user.id, richAcl?.acl ?? []);

	const { formatDate } = useLocaleFormatters();

	// A widget with no monitorable constraint never reaches here: the base walls it (see the mapper).
	return (
		<>
			{filteredRows.length === 0 && !isMarketViewExpired ? (
				<IconWalls.PortfolioMonitoringHiddenV2 opaque />
			) : (
				<div className="h-full flex flex-col min-h-0 w-full">
					<div className="h-full min-h-0 flex flex-col flex-1 mb-2">
						<ScrollWrapper className="pr-2">
							{isMarketViewExpired && marketViewMonitoringMetric && (
								<div
									className={toClassName({
										"flex flex-row gap-2 py-2 items-center justify-between border-b-[var(--palette-N100)]": true,
									})}
									style={{
										borderBottomWidth: 1,
									}}
								>
									<div>
										<div className="flex items-center gap-2">
											<div>
												<Text type="Body/M/Bold">Market view</Text>
												<Text type="Body/M/Book">
													{` - Expired on ${formatDate(marketViewMonitoringMetric.forecastDates?.to)}`}
												</Text>
											</div>
											{iconBySeverity.breached}
										</div>
										<div>
											<ActionText
												type="Body/M/Bold"
												href={
													marketViewMonitoringMetric.scenarioIdentifier === "CUSTOM_PORTFOLIO_MARKET_VIEW"
														? `${typedUrlForRoute("Portfolios/SettingsPortfolio", {
																// redirect to portfolio settings
																portfolioUid: portfolio?.uuid ?? "",
															})}?tab=2#marketView`
														: typedUrlForRoute("MarketViewWorkSpace/View", {
																uuid: marketViewMonitoringMetric.scenarioIdentifier!,
															})
												}
											>
												{!marketViewMonitoringMetric.scenarioName
													? `Market view ${formatDate(marketViewMonitoringMetric.forecastDates?.from)}`
													: marketViewMonitoringMetric.scenarioName}
											</ActionText>
										</div>
									</div>
								</div>
							)}
							{/* Flat row driven by a COMPOSITION_PRIORITY monitoring metric (always soft). */}
							{compositionHasPriority && (
								<div
									className={toClassName({
										"flex flex-row gap-2 py-2 items-center justify-between border-b-[var(--palette-N100)]": true,
									})}
									style={{ borderBottomWidth: 1 }}
								>
									<div>
										<Text type="Body/M/Bold">Composition has priority</Text>
										<Text type="Body/M/Book">{" - Soft"}</Text>
									</div>
								</div>
							)}
							<ForEach collection={groupedRows}>
								{(group) => {
									if (!group?.length) {
										return null;
									} else {
										const renderEntry = (
											entry: PortfolioMonitoringBlockEntry,
											opts?: {
												severity?: PortfolioMonitoringSeverity;
												bar?: boolean;
												innerRef?: (el: HTMLDivElement | null) => void;
												showType?: boolean;
												showConstraintInfo?: boolean;
												hideSubType?: boolean;
												style?: CSSProperties;
												textClassList?: string;
											},
										) => (
											<div
												ref={opts?.innerRef}
												className={toClassName({
													"flex flex-col py-2 border-b-[var(--palette-N100)]": true,
												})}
												style={{
													borderBottomWidth: 1,
													...opts?.style,
												}}
											>
												<div className="grow min-w-0 overflow-hidden">
													{(opts?.showType ?? true) && (
														<div className="flex items-center gap-2">
															<Text type="Body/M/Bold">
																{entry.type === "WEIGHT_ON_SINGLE_INSTRUMENT" || entry.type === "NUMBER_OF_INSTRUMENTS"
																	? entry.constraintRelation === "MAX"
																		? "Max "
																		: entry.constraintRelation === "MIN"
																			? "Min "
																			: ""
																	: ""}

																{t(`MONITORING_METRICS.${entry.type}`)}
																{(!hasApplicationMode[entry.type] || entry.type === "TAG") &&
																entry.type !== "GROUP_ASSET_ALLOCATION"
																	? ""
																	: entry.applicationMode
																		? " tag"
																		: " risk model"}
															</Text>

															{opts?.severity && iconBySeverity[opts.severity]}
														</div>
													)}

													{!opts?.hideSubType &&
														(entry.type === "TAG_RELATIVE_TO_TAG" ? (
															<div className="flex flex-wrap items-center gap-1" title={entry.subType}>
																<ForEach collection={entry.subType.split(" / ")}>
																	{(tag) => <TagBadge label={tag} color={mockTagColor(tag)} />}
																</ForEach>
															</div>
														) : (
															<Text
																type="Body/M/Book"
																className={{
																	"truncate w-full block": true,
																	[opts?.textClassList ?? ""]: opts?.textClassList !== undefined,
																}}
																title={entry.subType}
															>
																{entry.subType}
																{entry.constraintRelation === "RELATIVE_TO_REFERENCE" ? " - Rel to reference" : ""}
															</Text>
														))}
													{(entry.type !== "LOCK_INSTRUMENT" || !(opts?.showConstraintInfo ?? true)) && (
														<Text type="Body/M/Book">
															{createConstraintInfo({
																excludeSoftConstraint,
																excludeConstraintWithoutRelation,
																monitoringEntry: entry,
																t,
															})}
														</Text>
													)}
												</div>
												{entry.status === "processing" && (
													<Text type="Body/M/Book" className="!italic text-right">
														Processing...
													</Text>
												)}
												{(opts?.bar ?? true) && entry.status !== "processing" && (
													<div className="shrink">
														<PortfolioMonitoringIndicator
															{...entry}
															hidePercentage={isMetricTypeUnitless(entry.type)}
														/>
													</div>
												)}
											</div>
										);
										if (group.length === 1 && group.at(0)?.type !== "FOR_EACH") {
											return renderEntry(group[0], {
												style: { minHeight: 41 },
												hideSubType:
													group[0].type === "WEIGHT_ON_SINGLE_INSTRUMENT" ||
													group.at(0)?.type === "NUMBER_OF_INSTRUMENTS",
											});
										}
										return (
											<Controller value={false}>
												{({ value: expand, onChange: setExpand }) => (
													<>
														<button
															type="button"
															className="flex w-full text-left items-center py-2 border-b transition-[border-color]"
															onClick={() => setExpand(!expand)}
															style={{
																minHeight: 41,
																borderBottomColor: themeCSSVars.palette_N100,
																transitionDuration: `${defaultTailwindTransitionDurationMs}ms`,
															}}
														>
															<div className="grow min-w-0">
																{renderEntry(
																	{
																		...group[0],
																		type:
																			group.at(0)!.type === "ASSET_ALLOCATION"
																				? "GROUP_ASSET_ALLOCATION"
																				: group.at(0)!.type,
																		subType: generateSubType(group),
																		value:
																			group[0].type === "INSTRUMENT"
																				? sumArrayLike(group, (x) => x.value)
																				: group[0].type === "ASSET_ALLOCATION"
																					? (group.at(0)?.groupValue ?? group.find((x) => !x.subType)?.groupValue ?? 0)
																					: (group.at(0)?.value ?? 0),
																		severity:
																			group[0].type === "INSTRUMENT"
																				? getSeverityByRelation(group[0].constraintRelation, {
																						value: sumArrayLike(group, (x) => x.value),
																						maxThreshold: group[0].thresholds.max,
																						minThreshold: group[0].thresholds.min,
																						target: group[0].isSoftConstraint,
																						maxWarningDelta: group[0].warningDelta.max,
																						minWarningDelta: group[0].warningDelta.min,
																					})
																				: group[0].type === "ASSET_ALLOCATION"
																					? getSeverityByRelation(group[0].constraintRelation, {
																							value:
																								group.find((x) => !x.subType)?.groupValue ?? group.at(0)?.value ?? 0,
																							maxThreshold: group[0].thresholds.max,
																							minThreshold: group[0].thresholds.min,
																							target: group[0].isSoftConstraint,
																							maxWarningDelta: group[0].warningDelta.max,
																							minWarningDelta: group[0].warningDelta.min,
																						})
																					: group[0].severity,
																	},
																	{
																		severity: group[0].type === "ASSET_ALLOCATION" ? undefined : groupSeverity(group),
																		bar: !!(group[0].type === "INSTRUMENT" || group[0].type === "ASSET_ALLOCATION"),
																		style: { minHeight: 41, borderBottomWidth: 0 },
																	},
																)}
															</div>

															{group.at(0)?.status !== "processing" && (
																<span
																	style={{
																		transitionDuration: `${defaultTailwindTransitionDurationMs}ms`,
																	}}
																	className="transition-transform aria-expanded:transform-[rotateX(180deg)] flex shrink"
																	aria-expanded={expand}
																>
																	<Icon icon="Down" size={14} />
																</span>
															)}
														</button>
														<CollapsibleBase expand={expand} transitionDuration={defaultTailwindTransitionDurationMs}>
															<div className="py-px px-2" style={{ backgroundColor: themeCSSVars.palette_N20 }}>
																<ForEach collection={group}>
																	{(entry) =>
																		!entry.subType ? (
																			<></>
																		) : entry.type === "INSTRUMENT" || entry.type === "ASSET_ALLOCATION" ? (
																			<div
																				className={toClassName({
																					"flex flex-row py-2 border-b-[var(--palette-N100)]": true,
																				})}
																				style={{ borderBottomWidth: 1 }}
																			>
																				<Text
																					type="Body/M/Book"
																					className="flex-1 truncate block italic!"
																					title={entry.subType}
																					as="p"
																				>
																					{entry.subType}{" "}
																				</Text>

																				<Text type="Body/M/Medium" className="shrink-0 truncate block italic!" as="p">
																					{entry.value}%
																				</Text>
																			</div>
																		) : (
																			<div
																				className={toClassName({
																					"flex flex-col py-2 border-b-[var(--palette-N100)]": true,
																				})}
																				style={{ borderBottomWidth: 1 }}
																			>
																				<Text
																					type="Body/M/Book"
																					className="truncate w-full block italic!"
																					title={entry.subType}
																				>
																					{entry.subType}{" "}
																				</Text>
																				<PortfolioMonitoringIndicator
																					{...entry}
																					hidePercentage={isMetricTypeUnitless(entry.type)}
																				/>
																			</div>
																		)
																	}
																</ForEach>
															</div>
														</CollapsibleBase>
													</>
												)}
											</Controller>
										);
									}
								}}
							</ForEach>
						</ScrollWrapper>
					</div>
					<div className="flex justify-between">
						<Legend />
						{areSomeAlertBreached &&
							!enhanced &&
							(portfolio?.status === "READY" || portfolio?.status === "ACCEPTED") &&
							canCreateProposal && (
								<Button size="x-small" palette="secondary" onClick={() => setIsModalOpen(true)}>
									<Icon icon="Icon-full-small" className="mr-1" />
									{t("FIX_ISSUES")}
								</Button>
							)}
					</div>
				</div>
			)}

			<Dialog
				className="flex-1"
				show={isModalOpen}
				onClose={() => setIsModalOpen(false)}
				header={t("PORTFOLIOS.PORTFOLIO_OPTIMIZATION")}
				footer={({ state }) => (
					<DialogFooter
						primaryAction={
							<Button palette="primary" size="small" onClickAsync={confirmOptimization}>
								{t("PROCEED")}
							</Button>
						}
						neutralAction={
							<Button
								palette="tertiary"
								size="small"
								disabled={state === "loading"}
								onClick={() => setIsModalOpen(false)}
							>
								{t("LEAVE")}
							</Button>
						}
					/>
				)}
			>
				{t("PORTFOLIOS.ALICE_SUGGEST_ENHANCHED")}
			</Dialog>
		</>
	);
}

export default withContexts([PortfolioContext])(PortfolioMonitoringBlock);

/** @internal — exported for tests */
export function generateSubType(list: PortfolioMonitoringBlockEntry[]): string {
	const entry = list.at(0);
	let subType: string = "";

	if (!entry) {
		return subType;
	}

	const validList = list.filter((x) => x.subType);

	if (
		(entry.type === "TAG" || entry.type === "CURRENCY" || entry.type === "ASSET_ALLOCATION") &&
		entry.classificationOption &&
		validList.length === 0
	) {
		subType += `${entry.classificationOption} - `;
	}

	subType += `${validList.length}`;

	if (entry.type === "ASSET_ALLOCATION") {
		subType += validList.length > 1 ? " asset allocations" : " asset allocation";
		return subType;
	}

	subType += validList.length > 1 ? " instruments" : " instrument";
	return subType;
}

function LegendIcon({ fill }: { fill: string }): React.JSX.Element {
	return (
		<Svg viewBox={{ width: 12, height: 12 }}>
			<rect fill={fill} x="0" y="0" height="12" width="12" rx="2" ry="2" />
		</Svg>
	);
}

function Legend(): React.JSX.Element {
	return (
		<div className="flex gap-2 items-center">
			<div className="flex items-center">
				<LegendIcon fill={colorByPortfolioMonitoringSeverity.ok} />
				&nbsp;
				<Text type="Body/S/Book" color={themeCSSVars.palette_N500}>
					OK
				</Text>
			</div>
			<div className="flex items-center">
				<LegendIcon fill={colorByPortfolioMonitoringSeverity["close-to"]} />
				&nbsp;
				<Text type="Body/S/Book" color={themeCSSVars.palette_N500}>
					Close-To
				</Text>
			</div>
			<div className="flex items-center">
				<LegendIcon fill={colorByPortfolioMonitoringSeverity.breached} />
				&nbsp;
				<Text type="Body/S/Book" color={themeCSSVars.palette_N500}>
					Breached
				</Text>
			</div>
			<div className="flex items-center">
				<Svg viewBox={{ width: 2, height: 12 }}>
					<rect fill={themeCSSVars.palette_N300} x="0" y="0" height="12" width="2" rx="1" ry="1" />
				</Svg>
				&nbsp;
				<Text type="Body/S/Book" color={themeCSSVars.palette_N500}>
					Constraint
				</Text>
			</div>
		</div>
	);
}

export const portfolioMonitoringSeverity = ["ok", "close-to", "breached"] as const;
export type PortfolioMonitoringSeverity = (typeof portfolioMonitoringSeverity)[number];
export const colorByPortfolioMonitoringSeverity: Record<PortfolioMonitoringSeverity, string> = {
	ok: themeCSSVars.palette_S300,
	"close-to": themeCSSVars.palette_W300,
	breached: themeCSSVars.palette_D300,
};

const height = 28;
const barY = 14;
export function PortfolioMonitoringIndicator(props: PortfolioMonitoringBlockEntry): React.JSX.Element {
	const segmentsWidthPercentages = match(props)
		.with({ constraintRelation: "BETWEEN" }, { constraintRelation: "RELATIVE_TO_REFERENCE" }, () => [
			0, 30, 35, 65, 70, 100,
		])

		.with({ constraintRelation: "MIN" }, () => [0, 30, 35, 100])
		.with({ constraintRelation: "MAX" }, () => [0, 65, 70, 100])
		// Very close to better represent the equal constraint
		.with({ constraintRelation: "EQUAL" }, () => [0, 49.98, 49.99, 50, 50.01, 50.02, 100])
		.exhaustive();
	const segmentsValues = match(props)
		.with({ constraintRelation: "BETWEEN" }, (p) => {
			// handles relative values which can be arbitrarily large (e.g. -1200%)
			const range = {
				min: Math.min(p.min[0], 0),
				max: Math.max(p.max[1], 100),
			};
			return [
				range.min,
				Math.max(range.min + Number.EPSILON, p.min[0]),
				Math.max(range.min + 2 * Number.EPSILON, p.min[1]),
				Math.min(p.max[0], range.max - 2 * Number.EPSILON),
				Math.min(p.max[1], range.max - Number.EPSILON),
				range.max,
			];
		})
		.with({ constraintRelation: "RELATIVE_TO_REFERENCE" }, (p) => {
			// handles relative values which can be arbitrarily large (e.g. -1200%)
			const range = {
				min: Math.min(p.min[0], 0),
				max: Math.max(p.max[1], 100),
			};
			return [
				range.min,
				Math.max(range.min + Number.EPSILON, p.min[0]),
				Math.max(range.min + 3 * Number.EPSILON, p.min[1]),
				Math.min(p.max[0], range.max - 2 * Number.EPSILON),
				Math.min(p.max[1], range.max - Number.EPSILON),
				range.max,
			];
		})
		.with({ constraintRelation: "MIN" }, (p) => {
			// handles relative values which can be arbitrarily large (e.g. -1200%)
			const range = {
				min: Math.min(p.min[0], 0),
				max: Math.max(p.min[1], 100),
			};
			return [
				range.min,
				Math.max(range.min + Number.EPSILON, p.min[0]),
				Math.max(range.min + 2 * Number.EPSILON, p.min[1]),
				range.max,
			];
		})
		.with({ constraintRelation: "MAX" }, (p) => {
			// handles relative values which can be arbitrarily large (e.g. -1200%)
			const range = {
				min: Math.min(p.max[0], 0),
				max: Math.max(p.max[1], 100),
			};
			return [
				range.min,
				Math.min(p.max[0], range.max - 2 * Number.EPSILON),
				Math.min(p.max[1], range.max - Number.EPSILON),
				range.max,
			];
		})
		.with({ constraintRelation: "EQUAL" }, (p) => {
			// handles relative values which can be arbitrarily large (e.g. -1200%)
			const range = {
				min: Math.min(p.min[0], 0),
				max: Math.max(p.max[1], 100),
			};
			return [
				range.min,
				Math.max(range.min + Number.EPSILON, p.min[0]),
				Math.max(range.min + 2 * Number.EPSILON, p.min[1]),
				clamp(range.min + p.target, range.min + 3 * Number.EPSILON, range.max - 3 * Number.EPSILON),
				Math.min(p.max[0], range.max - 2 * Number.EPSILON),
				Math.min(p.max[1], range.max - Number.EPSILON),
				range.max,
			];
		})
		.exhaustive();

	const startSegmentIndex = Math.max(0, segmentsValues.findIndex((v) => v > props.value) - 1);
	const x =
		// center
		((props.value - segmentsValues[startSegmentIndex]) /
			// normalize
			(segmentsValues[startSegmentIndex + 1] - segmentsValues[startSegmentIndex])) *
			// scale up
			(segmentsWidthPercentages[startSegmentIndex + 1] - segmentsWidthPercentages[startSegmentIndex]) +
		// translate
		segmentsWidthPercentages[startSegmentIndex];
	const { formatNumber } = useLocaleFormatters();

	const between = () => (
		<line
			x1={`${segmentsWidthPercentages.at(1)}%`}
			x2={`${segmentsWidthPercentages.at(-2)}%`}
			y1={barY}
			y2={barY}
			strokeWidth={4}
			strokeLinecap="butt"
			stroke="#C3C9D5"
		/>
	);
	return (
		<div className="flex items-center">
			<ComputedSizeContainer className="grow min-w-[160px]">
				{({ width }) => (
					<Svg viewBox={{ width, height }}>
						<line x1={0} x2={width} y1={barY} y2={barY} strokeWidth={4} strokeLinecap="butt" stroke="#E1E4EA" />
						<Switch
							case={props.constraintRelation}
							match={{
								RELATIVE_TO_REFERENCE: between,
								BETWEEN: between,
								MIN: () => (
									<line
										x1={`${segmentsWidthPercentages.at(1)}%`}
										x2={`${segmentsWidthPercentages.at(-1)}%`}
										y1={barY}
										y2={barY}
										strokeWidth={4}
										strokeLinecap="butt"
										stroke="#C3C9D5"
									/>
								),
								MAX: () => (
									<line
										x1={`${segmentsWidthPercentages.at(0)}%`}
										x2={`${segmentsWidthPercentages.at(-2)}%`}
										y1={barY}
										y2={barY}
										strokeWidth={4}
										strokeLinecap="butt"
										stroke="#C3C9D5"
									/>
								),
								EQUAL: () => <></>,
							}}
						/>
						{(props.constraintRelation === "BETWEEN" ||
							props.constraintRelation === "RELATIVE_TO_REFERENCE" ||
							props.constraintRelation === "MIN") && (
							<>
								<line
									stroke="#A5AEC0"
									strokeWidth={2}
									strokeLinecap="round"
									x1={`${segmentsWidthPercentages.at(1)}%`}
									y1={barY + 8}
									x2={`${segmentsWidthPercentages.at(1)}%`}
									y2={barY - 8}
								/>
								{props.showStepValue ? (
									<ValueToText
										index={1}
										segmentsValues={segmentsValues}
										segmentsWidthPercentages={segmentsWidthPercentages}
										hidePercentage={props.hidePercentage}
									/>
								) : null}
							</>
						)}
						{(props.constraintRelation === "BETWEEN" ||
							props.constraintRelation === "RELATIVE_TO_REFERENCE" ||
							props.constraintRelation === "MAX") && (
							<>
								<line
									stroke="#A5AEC0"
									strokeWidth={2}
									strokeLinecap="round"
									x1={`${segmentsWidthPercentages.at(-2)}%`}
									y1={barY + 8}
									x2={`${segmentsWidthPercentages.at(-2)}%`}
									y2={barY - 8}
								/>
								{props.showStepValue ? (
									<ValueToText
										index={-2}
										segmentsValues={segmentsValues}
										segmentsWidthPercentages={segmentsWidthPercentages}
										hidePercentage={props.hidePercentage}
									/>
								) : null}
							</>
						)}
						{props.constraintRelation === "EQUAL" && (
							<>
								<line
									stroke="#A5AEC0"
									strokeWidth={2}
									strokeLinecap="round"
									x1={`${segmentsWidthPercentages.at(3)}%`}
									y1={barY + 8}
									x2={`${segmentsWidthPercentages.at(3)}%`}
									y2={barY - 8}
								/>
								{props.showStepValue ? (
									<ValueToText
										index={3}
										segmentsValues={segmentsValues}
										segmentsWidthPercentages={segmentsWidthPercentages}
										hidePercentage={props.hidePercentage}
									/>
								) : null}
							</>
						)}
						<line
							x1={0}
							x2={`${x}%`}
							y1={barY}
							y2={barY}
							strokeWidth={4}
							strokeLinecap="butt"
							stroke={colorByPortfolioMonitoringSeverity[props.severity]}
						/>{" "}
					</Svg>
				)}
			</ComputedSizeContainer>
			<div
				title={`${formatNumber(props.value)}${props.hidePercentage ? "" : "%"}`}
				className="font-bold shrink pl-2 w-18 truncate text-right"
				style={{ color: colorByPortfolioMonitoringSeverity[props.severity] }}
			>
				{`${formatNumber(props.value)}${props.hidePercentage ? "" : "%"}`}
			</div>
		</div>
	);
}

function ValueToText(params: {
	segmentsValues: number[];
	segmentsWidthPercentages: number[];
	index: number;
	hidePercentage?: boolean;
}) {
	const { formatNumber } = useLocaleFormatters();
	return (
		params.segmentsValues.at(params.index) && (
			<text
				textAnchor="middle"
				x={`${params.segmentsWidthPercentages.at(params.index)}%`}
				y={barY + 20}
				fontSize={10}
				fontFamily="Gotham"
				fill="#697796"
			>
				{params.hidePercentage
					? formatNumber(params.segmentsValues.at(params.index))
					: `${formatNumber(params.segmentsValues.at(params.index))}%`}
			</text>
		)
	);
}

const iconBySeverity: Record<PortfolioMonitoringSeverity, ReactNode> = {
	ok: null,
	"close-to": <Icon color={colorByPortfolioMonitoringSeverity["close-to"]} icon="Icon-full-alert" />,
	breached: <Icon color={colorByPortfolioMonitoringSeverity.breached} icon="Icon-full-alert" />,
};

/** @internal — exported for tests */
export function groupSeverity(group: PortfolioMonitoringBlockEntry[]): PortfolioMonitoringSeverity {
	return group.some((x) => x.severity === "breached")
		? "breached"
		: group.some((x) => x.severity === "close-to")
			? "close-to"
			: "ok";
}
